import { useEffect, useMemo, useState } from "react";
import { CheckCircle, CircleNotch, Info, ArrowsClockwise, XCircle, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatPeriod } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Captura() {
  const { companies, period, periods, setPeriod } = useApp();
  const [companyId, setCompanyId] = useState(null);
  const [variables, setVariables] = useState([]);
  const [values, setValues] = useState({});
  const [original, setOriginal] = useState({});
  const [savedCode, setSavedCode] = useState(null);
  const [savingCode, setSavingCode] = useState(null);
  const [category, setCategory] = useState("all");

  // Automatic (Odoo) tab state
  const [autoRows, setAutoRows] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);

  useEffect(() => { api.get("/variables").then((res) => setVariables(res.data)); }, []);
  useEffect(() => { if (companies.length && !companyId) setCompanyId(companies[0].id); }, [companies, companyId]);

  // Load manual captured values for the company+period
  useEffect(() => {
    if (!companyId || !period) { setValues({}); setOriginal({}); return; }
    api.get(`/data-entries?company_id=${companyId}&period=${period}`).then((res) => {
      const map = {};
      res.data.forEach((r) => (map[r.variable_id] = r.value));
      setValues(map);
      setOriginal(map);
    });
  }, [companyId, period]);

  // Load cached last Odoo values for the Automatic tab
  const loadAuto = () => {
    if (!companyId || !period) { setAutoRows([]); return; }
    api.get(`/odoo/last-values?company_id=${companyId}&period=${period}`).then((res) => setAutoRows(res.data));
  };
  useEffect(() => {
    loadAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, period]);

  const manualVars = useMemo(() => variables.filter((v) => v.source_type === "manual"), [variables]);
  const categories = useMemo(() => Array.from(new Set(manualVars.map((v) => v.category).filter(Boolean))), [manualVars]);
  const manualFiltered = manualVars.filter((v) => category === "all" || v.category === category);

  // Individual save: only the modified variable is persisted.
  const saveOne = async (code) => {
    if (!companyId || !period) { toast.error("Selecciona empresa y periodo."); return; }
    const raw = values[code];
    if (raw === "" || raw === null || raw === undefined) return;
    if (String(raw) === String(original[code] ?? "")) return; // unchanged
    setSavingCode(code);
    try {
      await api.post("/data-entries", {
        variable_id: code, company_id: companyId, period, value: parseFloat(raw), source: "manual",
      });
      setOriginal((o) => ({ ...o, [code]: parseFloat(raw) }));
      setSavedCode(code);
      setTimeout(() => setSavedCode((c) => (c === code ? null : c)), 1800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingCode(null);
    }
  };

  const queryOdoo = async () => {
    if (!companyId || !period) { toast.error("Selecciona empresa y periodo."); return; }
    setAutoLoading(true);
    try {
      const { data } = await api.post("/odoo/query", { company_id: companyId, period });
      setAutoRows(data);
      const ok = data.filter((r) => r.error == null).length;
      toast.success(`Consulta Odoo completada · ${ok}/${data.length} variables con dato`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Captura</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Variables manuales (editables) y automáticas de Odoo (solo lectura), por empresa y periodo.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4">
        <Select value={companyId || ""} onValueChange={setCompanyId}>
          <SelectTrigger className="h-9 w-[240px]" data-testid="capture-company-select">
            <SelectValue placeholder="Empresa" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={period || ""} onValueChange={setPeriod}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="capture-period-select">
            <SelectValue placeholder="Periodo" />
          </SelectTrigger>
          <SelectContent>
            {periods.map((p) => (<SelectItem key={p} value={p}>{formatPeriod(p)}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="manual" className="w-full">
        <TabsList data-testid="capture-tabs">
          <TabsTrigger value="manual" data-testid="tab-manual">Manual</TabsTrigger>
          <TabsTrigger value="auto" data-testid="tab-auto">Automático (Odoo)</TabsTrigger>
        </TabsList>

        {/* ---------------- MANUAL ---------------- */}
        <TabsContent value="manual" className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 w-[230px]" data-testid="capture-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info size={14} aria-hidden="true" /> El valor se guarda automáticamente al salir del campo (individual).
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="capture-grid">
            {manualFiltered.map((v) => (
              <div key={v.id} className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <label className="text-sm font-medium leading-snug" htmlFor={`var-${v.code}`}>{v.name}</label>
                  {savingCode === v.code ? (
                    <CircleNotch size={16} className="shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
                  ) : savedCode === v.code ? (
                    <CheckCircle size={16} weight="fill" className="shrink-0 text-success" data-testid={`saved-${v.code}`} aria-hidden="true" />
                  ) : null}
                </div>
                <p className="mb-2 font-mono text-[11px] text-muted-foreground">{v.code}</p>
                <div className="relative">
                  <Input
                    id={`var-${v.code}`}
                    type="number"
                    value={values[v.code] ?? ""}
                    onChange={(e) => setValues((s) => ({ ...s, [v.code]: e.target.value }))}
                    onBlur={() => saveOne(v.code)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    placeholder="0"
                    data-testid={`capture-input-${v.code}`}
                  />
                  {v.unit && (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      {v.unit}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {manualFiltered.length === 0 && (
              <p className="text-sm text-muted-foreground">No hay variables manuales en esta categoría.</p>
            )}
          </div>
        </TabsContent>

        {/* ---------------- AUTOMÁTICO ---------------- */}
        <TabsContent value="auto" className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock size={14} aria-hidden="true" /> Solo lectura · datos recuperados desde Odoo bajo demanda.
            </p>
            <Button onClick={queryOdoo} disabled={autoLoading || !companyId || !period} data-testid="odoo-query-button">
              {autoLoading ? <CircleNotch size={16} className="mr-1 animate-spin" aria-hidden="true" /> : <ArrowsClockwise size={16} className="mr-1" aria-hidden="true" />}
              {autoLoading ? "Consultando…" : "Consultar ahora"}
            </Button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="auto-table">
              <thead className="bg-accent/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Variable</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3 text-right">Último valor</th>
                  <th className="px-4 py-3">Última consulta</th>
                  <th className="px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {autoRows.map((r) => (
                  <tr key={r.variable_id} className="border-t border-border" data-testid={`auto-row-${r.variable_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{r.name}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{r.variable_id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.category}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                      {r.value != null ? `${r.value}${r.unit ? " " + r.unit : ""}` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{fmtDateTime(r.fetched_at)}</td>
                    <td className="px-4 py-2.5">
                      {r.error ? (
                        <span className="flex items-center gap-1 text-xs text-danger" title={r.error}>
                          <XCircle size={14} aria-hidden="true" /> {r.error.length > 40 ? r.error.slice(0, 40) + "…" : r.error}
                        </span>
                      ) : r.value != null ? (
                        <span className="flex items-center gap-1 text-xs text-success">
                          <CheckCircle size={14} aria-hidden="true" /> OK
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Sin consultar</span>
                      )}
                    </td>
                  </tr>
                ))}
                {autoRows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">No hay variables automáticas configuradas.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
